/**
 * @file backtestApi.ts
 * @description RTK Query API slice for backtest execution and quant report queries.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import type { TradingSymbol, Timeframe } from '@forex-builder/shared-types';
import type { RootState } from '../../../app/store.js';
import { createBaseQueryWithReauth } from '../../../shared/api/baseQueryWithReauth.js';
import { subscriptionApi } from '../../subscription/api/subscriptionApi.js';

export interface TradeRecordClient {
  tradeNumber: number;
  type: 'buy' | 'sell';
  symbol: TradingSymbol;
  entryTime: string;
  entryPrice: number;
  exitTime: string;
  exitPrice: number;
  lots: number;
  pnl: number;
  pips: number;
  exitReason: 'stop_loss' | 'take_profit' | 'trailing_stop' | 'opposite_signal' | 'session_end' | 'end_of_data';
  commission: number;
  swap: number;
}

export interface EquityPointClient {
  time: string;
  equity: number;
  drawdown: number;
  drawdownPercent: number;
}

export interface MonthlyPnLClient {
  year: number;
  month: number;
  pnl: number;
  tradesCount: number;
  winRate: number;
}

export interface BacktestReportClient {
  id: string;
  strategyId: string;
  strategyName: string;
  symbol: TradingSymbol;
  timeframe: Timeframe;
  from: string;
  to: string;
  initialBalance: number;
  finalBalance: number;
  netProfit: number;
  returnPercent: number;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  profitFactor: number;
  maxDrawdownAmount: number;
  maxDrawdownPercent: number;
  sharpeRatio: number;
  expectancy: number;
  avgWin: number;
  avgLoss: number;
  payoffRatio: number;
  equityCurve: EquityPointClient[];
  monthlyPnL: MonthlyPnLClient[];
  trades: TradeRecordClient[];
  executionTimeMs: number;
  createdAt: string;
}

export interface RunBacktestResponse {
  success: boolean;
  message?: string;
  data: {
    resultId: string;
    summary: BacktestReportClient;
  };
}

export interface GetBacktestReportResponse {
  success: boolean;
  data: {
    result: BacktestReportClient;
  };
}

export const backtestApi = createApi({
  reducerPath: 'backtestApi',
  tagTypes: ['BacktestSummary', 'BacktestHistory'],
  baseQuery: createBaseQueryWithReauth('/api/v1/backtest'),
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getUserSummary: builder.query<{
      success: boolean;
      data: {
        totalBacktests: number;
        backtestedStrategiesCount: number;
        bestReturnPercent: number;
        bestProfitFactor: number;
        bestStrategy: any;
        results: any[];
      };
    }, void>({
      query: () => '/user-summary',
      providesTags: ['BacktestSummary'],
    }),
    runBacktest: builder.mutation<RunBacktestResponse, { strategyId: string; initialBalance?: number; from?: string; to?: string }>({
      query: (body) => ({
        url: '/run',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BacktestSummary', 'BacktestHistory'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // even on error (e.g. 403 quota reached), refresh subscription data
        } finally {
          dispatch(subscriptionApi.util.invalidateTags(['Subscription']));
        }
      },
    }),
    getBacktestResult: builder.query<GetBacktestReportResponse, string>({
      query: (id) => `/results/${id}`,
    }),
    getBacktestHistory: builder.query<{ success: boolean; data: { history: BacktestReportClient[] } }, string>({
      query: (strategyId) => `/history/${strategyId}`,
      providesTags: ['BacktestHistory'],
    }),
    optimizeStrategy: builder.mutation<
      {
        success: boolean;
        data: {
          strategyName: string;
          parameterName: string;
          indicatorId: string;
          totalVariantsTested: number;
          bestVariant: {
            variantIndex: number;
            paramValue: number;
            netProfit: number;
            returnPercent: number;
            profitFactor: number;
            winRate: number;
            maxDrawdownPercent: number;
            sharpeRatio: number;
            totalTrades: number;
          };
          results: Array<{
            variantIndex: number;
            paramValue: number;
            netProfit: number;
            returnPercent: number;
            profitFactor: number;
            winRate: number;
            maxDrawdownPercent: number;
            sharpeRatio: number;
            totalTrades: number;
          }>;
        };
      },
      {
        strategyId: string;
        range: {
          indicatorId: string;
          paramName: string;
          start: number;
          end: number;
          step: number;
        };
      }
    >({
      query: (body) => ({
        url: '/optimize',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
        } finally {
          dispatch(subscriptionApi.util.invalidateTags(['Subscription']));
        }
      },
    }),
    runMonteCarlo: builder.mutation<
      {
        success: boolean;
        data: {
          totalIterations: number;
          initialBalance: number;
          confidenceInterval95: {
            minReturnPercent: number;
            maxReturnPercent: number;
            medianReturnPercent: number;
          };
          drawdownRisk: {
            probDrawdownOver10Pct: number;
            probDrawdownOver20Pct: number;
            probDrawdownOver30Pct: number;
            worstDrawdownPercent: number;
          };
          tradeStats: {
            avgConsecutiveLosses: number;
            maxConsecutiveLosses: number;
          };
        };
      },
      { resultId: string }
    >({
      query: (body) => ({
        url: '/monte-carlo',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
        } finally {
          dispatch(subscriptionApi.util.invalidateTags(['Subscription']));
        }
      },
    }),
    comparePairs: builder.mutation<
      {
        success: boolean;
        data: {
          strategyName: string;
          timeframe: string;
          bestPair: string;
          comparisons: Array<{
            symbol: TradingSymbol;
            netProfit: number;
            returnPercent: number;
            profitFactor: number;
            winRate: number;
            maxDrawdownPercent: number;
            totalTrades: number;
          }>;
        };
      },
      { strategyId: string }
    >({
      query: (body) => ({
        url: '/compare-pairs',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
        } finally {
          dispatch(subscriptionApi.util.invalidateTags(['Subscription']));
        }
      },
    }),
  }),
});

export const {
  useGetUserSummaryQuery,
  useRunBacktestMutation,
  useGetBacktestResultQuery,
  useGetBacktestHistoryQuery,
  useOptimizeStrategyMutation,
  useRunMonteCarloMutation,
  useComparePairsMutation,
} = backtestApi;
